import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from "react";
import {
  browserSessionToken,
  devLogin,
  getCurrentUser,
  readStoredToken,
  logout as requestLogout,
  type SessionUser,
  writeStoredToken,
} from "../api/client";

type AuthStatus = "loading" | "authenticated" | "anonymous";

type AuthContextValue = {
  status: AuthStatus;
  user: SessionUser | null;
  token: string | null;
  login(email: string, password: string): Promise<void>;
  logout(): Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<SessionUser | null>(null);
  const [token, setToken] = useState<string | null>(() => readStoredToken());

  useEffect(() => {
    let canceled = false;

    getCurrentUser(token)
      .then((currentUser) => {
        if (canceled) {
          return;
        }

        setUser(currentUser);
        setToken(currentUser === null ? null : browserSessionToken);
        setStatus(currentUser === null ? "anonymous" : "authenticated");
      })
      .catch(() => {
        if (canceled) {
          return;
        }

        writeStoredToken(null);
        setToken(null);
        setUser(null);
        setStatus("anonymous");
      });

    return () => {
      canceled = true;
    };
  }, [token]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      token,
      async login(email, password) {
        const session = await devLogin({ email, password });
        writeStoredToken(session.token);
        setToken(session.token);
        setUser(session.user);
        setStatus("authenticated");
      },
      async logout() {
        await requestLogout(token);
        writeStoredToken(null);
        setToken(null);
        setUser(null);
        setStatus("anonymous");
      },
    }),
    [status, token, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error("useAuth must be used within AuthProvider");
  }

  return context;
}
